import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircleIcon, ChevronDownIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAbsenceReportingSubmission, useAmendAbsenceReportingSubmission } from '../hooks/useAbsenceReporting';
import { useDirectory } from '../hooks/useDirectory';
import EmployeeLookup, { employeeOptionsFromDirectory } from './EmployeeLookup';
import { NODES, getVisiblePath, groupBySection, isAnswered } from './absenceReportingFlow';
import { REPORT_TYPE_LABEL } from './absenceReportingCategories';
import { toUkDateText, toUkTimeText, ukWallClockToDate } from '../utils/ukTime';

// Editing an already-filed record without leaving the HR dashboard — the
// same questions FSM's AbsenceReportingForm.jsx asks in its amend mode
// (absenceReportingFlow.js is a copy of FSM's), saved the same way: FSM's
// amendSubmission, through this app's proxy. That never overwrites the
// record — it files a new, amended entry with the reason given and marks
// the original superseded, exactly as when a manager edits it in FSM.

const INPUT = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';
const LONG_LABEL_THRESHOLD = 20;

// The flow stores dates as 'dd/MM/yyyy' text (FSM's DatePicker format);
// a native date input speaks 'yyyy-MM-dd'. Convert at the edge only.
function ukToIso(uk) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(uk || '');
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
}
function isoToUk(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}
// The record's own date/time is shown and corrected in UK time, whatever
// timezone the browser is in.
const formatUkDate = toUkDateText;
const formatTime = toUkTimeText;
// 'dd/MM/yyyy' + 'HH:mm' (UK) -> Date; null if the date doesn't parse.
const combineUkDateTime = ukWallClockToDate;

// Days Absent follows the picked Absence Start/End Dates — same count as
// FSM's AbsenceReportingForm.jsx (inclusive of the start date, exclusive of
// the end date, the day they came back). Without it, changing either date
// here kept the old Days Absent, which then printed on the report. null when
// the dates don't give a count (missing, or end before start).
function daysBetweenUk(startUk, endUk) {
  const s = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(startUk || '');
  const e = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(endUk || '');
  if (!s || !e) return null;
  const start = Date.UTC(Number(s[3]), Number(s[2]) - 1, Number(s[1]));
  const end = Date.UTC(Number(e[3]), Number(e[2]) - 1, Number(e[1]));
  return end < start ? null : Math.round((end - start) / 86400000);
}

// Same control choice as FSM's QuestionField: 3+ options (or forceDropdown)
// a dropdown, long labels radios, short ones (Yes/No) buttons.
function QuestionField({ node, value, onChange, employeeOptions }) {
  const isChoice = node.type === 'select' || node.type === 'ack';
  const useDropdown = isChoice && (node.options.length >= 3 || node.forceDropdown);
  const useRadio = isChoice && !useDropdown && node.options.some((o) => o.label.length > LONG_LABEL_THRESHOLD);
  const useButtons = isChoice && !useDropdown && !useRadio;

  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700">{node.text}</label>
      {node.helperText && <p className="mb-1.5 whitespace-pre-line text-xs text-gray-500">{node.helperText}</p>}

      {useDropdown && (
        <select value={value || ''} onChange={(e) => onChange(e.target.value)} className={INPUT}>
          <option value="">Select...</option>
          {node.options.map((o) => <option key={o.label} value={o.label}>{o.label}</option>)}
        </select>
      )}

      {useRadio && (
        <div className="space-y-1.5">
          {node.options.map((o) => (
            <label key={o.label} className="flex items-start gap-2 text-sm text-gray-700">
              <input type="radio" name={node.field} checked={value === o.label} onChange={() => onChange(o.label)} className="mt-0.5 text-brand focus:ring-brand" />
              {o.label}
            </label>
          ))}
        </div>
      )}

      {useButtons && (
        <div className="flex gap-2">
          {node.options.map((o) => {
            const active = value === o.label;
            const activeClass = o.label === 'Yes' ? 'border-green-600 bg-green-600 text-white' : o.label === 'No' ? 'border-red-600 bg-red-600 text-white' : 'border-brand bg-brand text-white';
            return (
              <button
                key={o.label}
                type="button"
                onClick={() => onChange(o.label)}
                className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium ${active ? activeClass : 'border-gray-300 text-gray-700 hover:border-brand'}`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      )}

      {node.type === 'employeeEmail' && (
        <EmployeeLookup value={value} onChange={onChange} options={employeeOptions} clearable={false} className="w-full" />
      )}
      {node.type === 'date' && <input type="date" value={ukToIso(value)} onChange={(e) => onChange(isoToUk(e.target.value))} className={INPUT} />}
      {node.type === 'time' && <input type="time" value={value || ''} onChange={(e) => onChange(e.target.value)} className={INPUT} />}
      {node.type === 'text' && (
        <input
          type="text"
          inputMode={node.numeric ? 'numeric' : undefined}
          value={value || ''}
          onChange={(e) => onChange(node.numeric ? e.target.value.replace(/[^0-9]/g, '') : e.target.value)}
          className={INPUT}
        />
      )}
      {node.type === 'textarea' && <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={3} className={INPUT} />}
      {node.type === 'computed' && <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">{value || '—'}</p>}
    </div>
  );
}

function AbsenceRecordEditor({ id, onClose }) {
  const queryClient = useQueryClient();
  const { data: existing, isLoading, error: loadError } = useAbsenceReportingSubmission(id, true);
  const amend = useAmendAbsenceReportingSubmission();
  const { data: directory } = useDirectory();
  const [answers, setAnswers] = useState(null);
  const [openSections, setOpenSections] = useState({});
  const [amendDate, setAmendDate] = useState('');
  const [amendTime, setAmendTime] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [validationError, setValidationError] = useState('');
  const [saved, setSaved] = useState(false);

  // Loaded once: the record's own answers, with every section open —
  // someone editing here is correcting something specific, not stepping
  // through a new report.
  useEffect(() => {
    if (!existing || answers) return;
    setAnswers({ ongoingAbsence: 'No', ...existing.answers });
    // When it actually happened: an earlier edit's corrected eventAt, else
    // when it was filed. Never submittedAt alone — an edited record's
    // submittedAt is the time of that edit, so editing it again reset the
    // date to today (reported by HR, 2026-09-28).
    const submitted = new Date(existing.eventAt || existing.submittedAt);
    setAmendDate(formatUkDate(submitted));
    setAmendTime(formatTime(submitted));
  }, [existing, answers]);

  const employeeOptions = useMemo(
    () => employeeOptionsFromDirectory(directory, [existing?.answers?.employeeEmail]),
    [directory, existing]
  );

  const reportType = answers?.reportType;
  const { path, terminal } = answers ? getVisiblePath(answers) : { path: [], terminal: null };
  const visiblePath = path.filter((nodeId) => !NODES[nodeId].hidden);
  const sections = groupBySection(visiblePath);

  function updateAnswer(field, value) {
    setValidationError('');
    setAnswers((a) => {
      const next = { ...a, [field]: value };
      // A picked date always carries through to what's worked out from it.
      if (field === 'absenceStartDate' || field === 'absenceEndDate') {
        const days = daysBetweenUk(next.absenceStartDate, next.absenceEndDate);
        if (days !== null) next.daysAbsent = String(days);
      }
      return next;
    });
  }

  async function handleSave() {
    setValidationError('');
    if (!terminal) {
      const missing = NODES[path[path.length - 1]];
      setValidationError(`Please answer: "${missing.section} — ${missing.text}"`);
      setOpenSections((prev) => ({ ...prev, [missing.section]: true }));
      return;
    }
    if (!changeReason.trim()) {
      setValidationError('Please say why this record is being changed.');
      return;
    }
    const amendedAt = combineUkDateTime(amendDate, amendTime);
    if (!amendedAt) {
      setValidationError('Please enter a valid date for when this actually happened.');
      return;
    }
    // reportType is never sent — an amend changes what's on a record, not
    // what kind of record it is (same as FSM's own form).
    const { reportType: _reportType, ...rest } = answers;
    try {
      await amend.mutateAsync({ id, changeReason: changeReason.trim(), submittedAt: amendedAt.toISOString(), ...rest });
      queryClient.invalidateQueries({ queryKey: ['absence-reporting-submissions'] });
      setSaved(true);
    } catch {
      // amend.error renders below.
    }
  }

  const saveError = amend.error?.response?.data?.error || (amend.isError ? 'Could not save — please try again.' : null);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-6 py-4">
          <div>
            <p className="text-sm font-semibold text-gray-900">Edit {REPORT_TYPE_LABEL[reportType] || 'record'}</p>
            <p className="mt-0.5 text-xs text-gray-500">Saving creates a new, amended entry — the original stays on record.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="-mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading && <p className="text-sm text-gray-400">Loading record…</p>}
          {loadError && <p className="text-sm text-rose-700">Could not load this record: {loadError.response?.data?.error || loadError.message}</p>}

          {saved && (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <CheckCircleIcon className="h-10 w-10 text-green-600" />
              <p className="text-sm font-semibold text-gray-900">Amended record saved</p>
              <p className="text-xs text-gray-500">The new entry shows in the lists, with the original marked as edited.</p>
            </div>
          )}

          {answers && !saved && (
            <div className="space-y-3">
              {sections.map((group) => {
                const isOpen = openSections[group.section] ?? true;
                const answered = group.nodeIds.filter((nodeId) => isAnswered(NODES[nodeId], answers)).length;
                return (
                  <div key={group.section} className="rounded-md border border-gray-200">
                    <button
                      type="button"
                      onClick={() => setOpenSections((prev) => ({ ...prev, [group.section]: !isOpen }))}
                      className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left"
                    >
                      <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-700">
                        {isOpen ? <ChevronDownIcon className="h-4 w-4 text-gray-400" /> : <ChevronRightIcon className="h-4 w-4 text-gray-400" />}
                        {group.section}
                      </span>
                      <span className="text-xs text-gray-400">{answered}/{group.nodeIds.length} answered</span>
                    </button>
                    {isOpen && (
                      <div className="space-y-4 border-t border-gray-100 p-3">
                        {group.nodeIds.map((nodeId) => (
                          <QuestionField
                            key={nodeId}
                            node={NODES[nodeId]}
                            value={answers[NODES[nodeId].field]}
                            onChange={(v) => updateAnswer(NODES[nodeId].field, v)}
                            employeeOptions={employeeOptions}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {reportType !== 'Return to Work' && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">When this actually happened</label>
                  <p className="mb-1.5 text-xs text-gray-500">Corrects the record's own date — defaults to when it was originally filed.</p>
                  <input type="date" value={ukToIso(amendDate)} onChange={(e) => setAmendDate(isoToUk(e.target.value))} className={INPUT} />
                </div>
              )}

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Reason for change</label>
                <p className="mb-1.5 text-xs text-gray-500">Required — shown on the record alongside what changed.</p>
                <textarea value={changeReason} onChange={(e) => setChangeReason(e.target.value)} rows={2} className={INPUT} />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-6 py-3">
          {(validationError || saveError) && <p className="mr-auto text-xs text-rose-700">{validationError || saveError}</p>}
          {saved ? (
            <button type="button" onClick={onClose} className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark">Close</button>
          ) : (
            <>
              <button type="button" onClick={onClose} className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!answers || amend.isPending}
                className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
              >
                {amend.isPending ? 'Saving…' : 'Save as amended record'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AbsenceRecordEditor;
